import { useCallback, useEffect, useRef, useState } from 'react';
import { Site, SiteBorder } from '../../confi-state';
import styles from './SiteConf.module.css';
import { useForm } from 'react-hook-form';
import { GeolocationHelper, GeoSearch } from '../../common/address-to-geoloc';
import { ServiceManager } from '../../services/service-manager';
import { TextField, Button, IconButton, Tooltip, Accordion, AccordionSummary, Typography, AccordionDetails } from '@mui/material';
import { Done, Replay, Undo } from '@mui/icons-material';
import { nlCartiqoLayersEnum } from '../../common/models/nl-cartiqo-layers.enum';
import { usePrevious } from '../../common/react-helpers';
import { ParcelFeature } from '../../models/parcel-feature';
import { Polygon } from '../../common/models/polygon';
import { Features } from '../../models/features';
import { ZoningFeature } from '../../models/zoning-feature';
import { DimensionFeature } from '../../models/dimensions-feature';
import { config } from '../../app-confi/app-config';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnderlayEdit from './UnderlayEdit/UnderlayEdit';
import { explanationIds } from '../../common/authentication/models/user';
import { isProdOrDevEnv } from '../../environment.enum';
import { severity } from '../../common/models/severity.enum';

const SiteConf = ({ currentSite, siteAddressFromModal, hoveredContextItem, pickedContextItem, loadedFeatures, changingContextVisibility, drawingParcel, pickingParcels, editingUnderlay, addSpinner, removeSpinner, onChangeSite, setLoadedFeatures, setDrawingParcel, setPickingParcels, setEditingUnderlay }:
    {
        currentSite: Site,
        siteAddressFromModal: { address: string };
        hoveredContextItem: { id: string },
        pickedContextItem: { id: string },
        loadedFeatures: Features,
        changingContextVisibility: boolean;
        drawingParcel: boolean;
        pickingParcels: boolean;
        editingUnderlay: boolean;
        addSpinner: (id: string, message: string) => void,
        removeSpinner: (id: string) => void,
        onChangeSite: (projectId: number | string, site: Site) => void,
        setLoadedFeatures: (features: Features) => void,
        setDrawingParcel: (r: boolean) => void,
        setPickingParcels: (r: boolean) => void,
        setEditingUnderlay: (r: boolean) => void
    }) => {
    const [siteName, setSiteName] = useState('');
    const [searchResult, setSearchResult] = useState<{ lat: string, lon: string, display_name: string }[]>(undefined);
    const [showBorderDraw, setShowBorderDraw] = useState(false);
    const [pickedParcels, setPickedParcels] = useState<string[]>([]);
    const [nameError, setNameError] = useState<{
        error: boolean,
        message: string
    }>({ error: undefined, message: undefined });

    const { register, reset, handleSubmit, formState: { errors } } = useForm<GeoSearch>();
    const [parcelExpansions, setParcelExpansions] = useState<{ pid: string, expanded: boolean }[]>([]);
    const firstRenderRef = useRef(true);

    const unselectedSiteAlpha = 0.05;
    const selectedSiteAlpha = 0.98;
    const selectedAndHoveredAlpha = 1;
    const unselectedAndHoveredAlpha = 0.7;

    const previousHoveredItem = usePrevious<{ id: string }>(hoveredContextItem);

    const validateName = (name: string) => {
        let valid = true;
        if (name.length < 1) {
            valid = false;
            setNameError({ error: true, message: 'Kavel naam heeft geen karakters.' })
        } else if (name.length > config.shortFieldSize) {
            valid = false;
            setNameError({ error: true, message: `Kavel naam heeft meer dan ${config.shortFieldSize} karakters.` })
        } else {
            setNameError({ error: false, message: undefined })
        }
        return valid;
    }

    useEffect(() => {
        if (currentSite.lon && currentSite.lat) {
            setShowBorderDraw(true);
        }
    }, [currentSite.lon, currentSite.lat])

    useEffect(() => {
        reset(currentSite.geoSearch);
    }, [currentSite.geoSearch, reset]);

    useEffect(() => {
        validateName(currentSite.name);
        setSiteName(currentSite.name);
    }, [currentSite.name])

    useEffect(() => {
        if (siteAddressFromModal) {
            if (!isProdOrDevEnv() && firstRenderRef.current) {
                firstRenderRef.current = false;
                return;
            }
            setSearchResult(undefined);
            onSubmit({ query: siteAddressFromModal.address });
        }
    }, [siteAddressFromModal])

    useEffect(() => {
        if (currentSite.border && currentSite.border.parcels && currentSite.border.parcels.length > 0) {
            const parcels = currentSite.border.parcels.map(p => p.originalid);
            setPickedParcels(parcels);
            const expansionParcels = parcels.map((pid) => ({ pid, expanded: false }));
            setParcelExpansions(expansionParcels);
        }
    }, [currentSite.border])

    useEffect(() => {
        if (pickingParcels) {
            ServiceManager.sceneManager.enabledHoverTracking = true;
        } else {
            ServiceManager.sceneManager.enabledHoverTracking = false;
        }
    }, [pickingParcels]);

    useEffect(() => {
        if (pickingParcels && hoveredContextItem && previousHoveredItem && previousHoveredItem.id !== hoveredContextItem.id) {

            if ((previousHoveredItem as any).type === nlCartiqoLayersEnum.parcels) {
                ServiceManager.sceneManager.contextService.setAlphaToContextMesh(previousHoveredItem.id, unselectedSiteAlpha);
                ServiceManager.sceneManager.contextService.setAlphaToContextMesh(hoveredContextItem.id, unselectedAndHoveredAlpha);

                pickedParcels.forEach(id => {
                    if (id !== hoveredContextItem.id) {
                        ServiceManager.sceneManager.contextService.setAlphaToContextMesh(id, selectedSiteAlpha);
                    } else {
                        ServiceManager.sceneManager.contextService.setAlphaToContextMesh(id, selectedAndHoveredAlpha);
                    }
                })
            }
        }
    }, [hoveredContextItem, pickingParcels, previousHoveredItem, pickedParcels])

    const updateParcelExpansions = (parcels: string[]) => {
        const expansionParcels = parcels.map((pid, index) => ({ pid, expanded: (parcels.length === index + 1) }));
        setParcelExpansions(expansionParcels);
    }

    useEffect(() => {
        if (pickingParcels && (pickedContextItem as any).type === nlCartiqoLayersEnum.parcels) {
            if (pickedParcels.some(p => p === pickedContextItem.id)) {
                // should unselect existing parcel
                ServiceManager.sceneManager.contextService.setAlphaToContextMesh(pickedContextItem.id, unselectedAndHoveredAlpha);
                const parcels = pickedParcels.filter(id => id !== pickedContextItem.id);
                updateParcelExpansions(parcels);
                setPickedParcels(parcels);
            } else {
                // add new parcel
                ServiceManager.sceneManager.contextService.setAlphaToContextMesh(pickedContextItem.id, selectedSiteAlpha);
                const parcels = [...pickedParcels, pickedContextItem.id];
                if (parcels.length === 1) {
                    ServiceManager.explanationService.showExplanation(explanationIds.siteSelectionData)
                }
                updateParcelExpansions(parcels);
                setPickedParcels(parcels);
            }
        }
        //eslint-disable-next-line
    }, [pickedContextItem])

    const onSubmit = (data: GeoSearch) => {
        const geoHelp = new GeolocationHelper();
        const id = 'locations';
        addSpinner(id, 'Locaties worden opgehaald...')

        function isPerceelId(str) {
            return /^(?=.*[a-zA-Z])(?=.*\d)[^\s]+$/.test(str);
        }
        if (isPerceelId(data.query)) {
            geoHelp.getCoordinatesByPerceelnummer(data.query).then(s => {
                removeSpinner(id);
                if (s) {
                    setSearchResult([{ ...s.wgs84, display_name: data.query }]);
                    const cs = { ...currentSite };
                    cs.geoSearch = { ...data };
                    if (s.wgs84) {
                        const coord = s.wgs84;
                        cs.lon = +coord.lon;
                        cs.lat = +coord.lat;
                        loadMap(cs.lon, cs.lat, cs, s.perceelnummer);
                    } else {
                        ServiceManager.snackbarService.openSnackbar('Geen locatie gevonden, probeer andere id.', severity.error);
                        onChangeSite(cs.projectId, cs)
                    }
                }
            });
        } else {
            geoHelp.adressToLatLongViaOpenStreetMap({ ...data, country: 'Netherlands' }).then(s => {
                removeSpinner(id);
                if (s && s.data) {
                    setSearchResult(s.data);
                    const cs = { ...currentSite };
                    cs.geoSearch = { ...data, country: 'Netherlands' };
                    if (s.data.length > 0) {
                        const coord = s.data[0];
                        cs.lon = +coord.lon;
                        cs.lat = +coord.lat;
                        loadMap(cs.lon, cs.lat, cs);
                    } else {
                        ServiceManager.snackbarService.openSnackbar('Geen adressen gevonden, probeer een ander adres.', severity.error);
                        onChangeSite(cs.projectId, cs)
                    }
                }
                else {
                    ServiceManager.snackbarService.openSnackbar('Geen adressen gevonden, probeer een ander adres.', severity.error);
                }
            }).catch(e => {
                removeSpinner(id);
            });
        }
    }

    const loadMap = (lon: number, lat: number, siteToUse?: Site, exactParcel?: number) => {
        const cs = siteToUse ? { ...siteToUse } : { ...currentSite };
        cs.lon = lon;
        cs.lat = lat;
        const id = 'map';
        addSpinner(id, 'Omgeving wordt geladen...')
        ServiceManager.babylonService.loadSite(lon, lat, [], [], undefined).then((res) => {
            let pcs = [];
            if (exactParcel) {
                const parcels = res.features.parcels;
                const parcel = parcels.find(p => p.props.parcel === exactParcel);
                pcs.push({...parcel.props});
                ServiceManager.babylonService.sceneManager.contextService.showContextMesh(parcel.props.originalid);
                ServiceManager.babylonService.sceneManager.contextService.setAlphaToContextMesh(parcel.props.originalid, 0);
            }

            cs.context = [];
            cs.border = { polygons: [], parcels: pcs };
            cs.options = { buildingsVisible: true, treesVisible: true, mapVisible: true, gridVisible: true, underlayVisible: true };
            setPickedParcels([]);
            if (res.features) {
                setLoadedFeatures(res.features);
            }
            onChangeSite(cs.projectId, cs)
            removeSpinner(id);
        });
    }


    const drawSite = () => {
        ServiceManager.explanationService.showExplanation(explanationIds.siteDrawDraw);
        ServiceManager.babylonService.sceneManager.startDrawPolyline();
        setDrawingParcel(true);
    }

    const stopDrawing = () => {
        setDrawingParcel(false);
        const path = ServiceManager.babylonService.sceneManager.stopDraw();
        if (path.length > 2) {
            const cs = { ...currentSite };
            cs.border = { polygons: [{ path, holes: [] }], parcels: [] };
            onChangeSite(cs.projectId, cs)
            ServiceManager.babylonService.sceneManager.contextService.hideContextTypes([nlCartiqoLayersEnum.parcels]);
            setPickedParcels([]);
        }
    }

    const resetDrawing = () => {
        ServiceManager.babylonService.sceneManager.resetPolyline();
    }

    const undoLastPoint = () => {
        ServiceManager.babylonService.sceneManager.undoLastPolylinePoint();
    }

    const selectSite = () => {
        setPickingParcels(true);
        ServiceManager.explanationService.showExplanation(explanationIds.siteSelectionPick);
        ServiceManager.babylonService.sceneManager.contextService.hoverEnabled = true;
        ServiceManager.babylonService.sceneManager.contextService.hideContextTypes([nlCartiqoLayersEnum.buildings, nlCartiqoLayersEnum.trees]);
        ServiceManager.babylonService.sceneManager.contextService.showContextTypes([nlCartiqoLayersEnum.parcels], unselectedSiteAlpha, true);
        if (currentSite.border && currentSite.border.parcels) {
            currentSite.border.parcels.forEach(p => {
                ServiceManager.babylonService.sceneManager.contextService.setAlphaToContextMesh(p.originalid, selectedSiteAlpha);
            })
        }
        ServiceManager.sceneManager.modelLoaderService.disableGizmosInAllGroupControls();
    }

    const resetPickingSite = () => {
        if (currentSite.border && currentSite.border.parcels) {
            currentSite.border.parcels.forEach(p => {
                ServiceManager.babylonService.sceneManager.contextService.setAlphaToContextMesh(p.parcel, selectedSiteAlpha);
            })
        } else {
            setPickedParcels([]);
        }
        ServiceManager.babylonService.sceneManager.contextService.showContextTypes([nlCartiqoLayersEnum.parcels], unselectedSiteAlpha, true);
        currentSite.border.parcels.forEach(p => {
            ServiceManager.babylonService.sceneManager.contextService.setAlphaToContextMesh(p.parcel, selectedSiteAlpha);
        })
    }

    const stopPickingSite = () => {
        setPickingParcels(false);
        ServiceManager.babylonService.sceneManager.contextService.hoverEnabled = false;
        ServiceManager.babylonService.sceneManager.contextService.hideContextTypes([nlCartiqoLayersEnum.parcels]);

        const context = currentSite.context;
        const options = currentSite.options;

        if (!options || options.buildingsVisible) {
            ServiceManager.babylonService.sceneManager.contextService.showContextTypes([nlCartiqoLayersEnum.buildings]);
        }
        if (!options || options.treesVisible) {
            ServiceManager.babylonService.sceneManager.contextService.showContextTypes([nlCartiqoLayersEnum.trees]);
        }

        if (context && context.length > 0) {
            if (!options || options.buildingsVisible) {
                const buildings = context.filter(s => s.type === nlCartiqoLayersEnum.buildings);
                buildings.forEach(b => {
                    if (!b.visible) {
                        ServiceManager.babylonService.sceneManager.contextService.hideContextItem(b.id);
                    }
                })
            }
            if (!options || options.treesVisible) {
                const trees = context.filter(s => s.type === nlCartiqoLayersEnum.trees);
                trees.forEach(t => {
                    if (!t.visible) {
                        ServiceManager.babylonService.sceneManager.contextService.hideContextItem(t.id);
                    }
                })
            }
        }

        if (pickedParcels.length > 0) {
            const parcelPolygons: Polygon[] = [];
            const parcels: ParcelFeature[] = [];
            pickedParcels.forEach(id => {
                ServiceManager.babylonService.sceneManager.contextService.showContextMesh(id);
                ServiceManager.babylonService.sceneManager.contextService.setAlphaToContextMesh(id, 0);
                const res = loadedFeatures.parcels.find(s => s.id === id);
                const parcelBorders: Polygon[] = [];
                if (res) {
                    parcelPolygons.push(...res.polygons);
                    parcelBorders.push(...res.polygons);
                    parcels.push({ ...res.props });
                }
            });

            const border: SiteBorder = {
                parcels,
            }

            onChangeSite(currentSite.projectId, { ...currentSite, border });
            ServiceManager.babylonService.sceneManager.cleanPolyline();
        }

        ServiceManager.sceneManager.modelLoaderService.enableGizmosInAllGroupControls();
    }


    const getZonesForParcel = useCallback((feature: ParcelFeature): { id: string, featureId: number, props: ZoningFeature }[] => {
        let result = [];
        if (feature.zones) {
            const parsedZones = JSON.parse(feature.zones) as number[];
            result = parsedZones.map(zoneFeatureId => loadedFeatures.zones.find(z => z.featureId === zoneFeatureId)).filter(x => x !== undefined);
        }
        return result;
    }, [loadedFeatures])

    const getZoneTypeSetText = useCallback((feature: ParcelFeature): string => {
        let result = '';
        const zones = getZoneTypeSet(feature);
        if (zones.length > 1) {
            result = zones.join(', ');
        } else if (zones.length === 1) {
            result = zones[0];
        }
        return result;
    }, [getZonesForParcel])

    const getZoneTypeSet = useCallback((feature: ParcelFeature): string[] => {
        let result = [];
        const zonesForParcel = getZonesForParcel(feature).filter(s => s !== undefined);;
        const zonings = zonesForParcel.map(zone => zone.props.zoning);
        if (zonings && zonings.length > 0) {
            result = Array.from(new Set(zonings));
        }
        return result;
    }, [getZonesForParcel])

    const getDimensionsForParcel = useCallback((feature: ParcelFeature): { id: number, props: DimensionFeature }[] => {
        let result = [];
        if (feature.dimensions) {
            const parsedDimensions = JSON.parse(feature.dimensions) as number[];
            result = parsedDimensions.map(dimension => loadedFeatures.dimensions.find(d => d.featureId === dimension)).filter(x => x !== undefined);
        }
        return result;
    }, [loadedFeatures])

    const getDimensionMaxBuildingHeightsText = useCallback((feature: ParcelFeature): string => {
        let result = '';
        const heights = getDimensionMaxBuildingHeightSet(feature).filter(s => s !== undefined);
        if (heights.length > 1) {
            result = heights.join(', ');
        } else if (heights.length === 1) {
            result = heights[0];
        }
        return result;
    }, [getDimensionsForParcel])

    const getDimensionMaxBuildingHeightSet = useCallback((feature: ParcelFeature): string[] => {
        let result = [];
        const dimensionsForParcel = getDimensionsForParcel(feature);
        const maxBuildingHeights = dimensionsForParcel.map(dimension => dimension.props.max_building_height);
        if (maxBuildingHeights && maxBuildingHeights.length > 0) {
            result = Array.from(new Set(maxBuildingHeights));
        }
        return result;
    }, [getDimensionsForParcel])

    const handleExpand = (pid: string) => {
        const pe = [...parcelExpansions.map(pe => pe.pid === pid ? { ...pe, expanded: !pe.expanded } : { ...pe, expanded: false })]
        setParcelExpansions(pe);
    }

    return currentSite && (
        <div className="group" data-testid="SiteConf">
            {
                !editingUnderlay &&
                <>
                    {(!pickingParcels && !drawingParcel) &&
                        <>
                            <h2>Kavel</h2>
                            <div>
                                <form onSubmit={(handleSubmit(onSubmit))}>
                                    <div>
                                        <TextField
                                            id="adres-query"
                                            className={`${styles.search} form-input`}
                                            label="Adres of percel id"
                                            autoFocus={true}
                                            {...register('query', { required: { value: true, message: 'Adres moet ingevuld zijn.' } })}
                                            variant="standard"
                                        />
                                        {errors.query && <p className="field-error">{errors.query.message}</p>}
                                    </div>
                                    <Button color="primary" size="small" variant="outlined" className="form-submit" type="submit">Zoeken</Button>
                                </form>
                                {searchResult &&
                                    <div>
                                        <Accordion>
                                            <AccordionSummary
                                                className={styles.accordionHeader}
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="zoek-resultaten"
                                            >
                                                <Typography>Zoek resultaten</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {searchResult.map(res => {
                                                    return (
                                                        <div key={res.lon + res.lat} className={styles.searchRes}>
                                                            {res.display_name}
                                                            <div>
                                                                Latitude: {res.lat}
                                                            </div>
                                                            <div>
                                                                Longitude: {res.lon}
                                                            </div>
                                                            <Button color="primary" className={styles.buttonMargin} size="small" variant="outlined" onClick={() => { loadMap(+res.lon, +res.lat) }}>Omgeving Inladen</Button>
                                                        </div>
                                                    )
                                                })}
                                            </AccordionDetails>
                                        </Accordion>

                                    </div>}
                            </div>
                        </>
                    }
                </>
            }
            {currentSite.lon && currentSite.lat && !pickingParcels && !drawingParcel &&
                <UnderlayEdit currentSite={currentSite} onChangeSite={onChangeSite} editingUnderlay={editingUnderlay} setEditingUnderlay={setEditingUnderlay} />
            }
            {!editingUnderlay &&
                <>

                    {showBorderDraw &&
                        <>
                            <div className="actions">
                                {drawingParcel &&
                                    <div>
                                        <Tooltip title="Opnieuw Tekenen">
                                            <IconButton data-explanation-id={explanationIds.siteDrawAccept} color="primary" aria-label="opnieuw tekenen" component="span" onClick={() => { resetDrawing() }}>
                                                <Replay />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Terug">
                                            <IconButton color="primary" aria-label="terug vorige stap" component="span" onClick={() => { undoLastPoint() }}>
                                                <Undo />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Klaar">
                                            <IconButton color="primary" aria-label="klaar met tekenen" component="span" onClick={() => { stopDrawing() }}>
                                                <Done />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                }
                                {pickingParcels &&
                                    <div>
                                        <Tooltip title="Opnieuw Selecteren">
                                            <IconButton data-explanation-id={explanationIds.siteSelectionData} color="primary" aria-label="opnieuw perceelen selecteren" component="span" onClick={() => { resetPickingSite() }}>
                                                <Replay />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Klaar">
                                            <IconButton color="primary" aria-label="klaar met selecteren" component="span" onClick={() => { stopPickingSite() }}>
                                                <Done />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                }
                                {(!(drawingParcel || pickingParcels) && !changingContextVisibility) &&
                                    <>
                                        <Button data-explanation-id={explanationIds.initialSelectSite} color="primary" className={styles.buttonMargin} size="small" variant="outlined" onClick={() => { selectSite() }}>Kavel selecteren</Button>
                                        <Button color="primary" className={styles.buttonMargin} size="small" variant="outlined" onClick={() => { drawSite() }}>Kavelgrens tekenen</Button>
                                    </>
                                }
                                {
                                    pickedParcels && loadedFeatures && pickedParcels.length > 0 && parcelExpansions.length === pickedParcels.length &&
                                    <div className={styles.accordionTop}>
                                        {pickedParcels.map((pid, index) => {
                                            const parcelFeature = loadedFeatures.parcels.find(f => f.id === pid);
                                            return parcelFeature && parcelFeature.props && (
                                                <Accordion key={pid} expanded={parcelExpansions?.find(p => p.pid === pid).expanded} onChange={() => handleExpand(pid)}>
                                                    <AccordionSummary
                                                        className={styles.accordionHeader}
                                                        expandIcon={<ExpandMoreIcon />}
                                                        aria-controls="panel1a-content"
                                                    >
                                                        <Typography>Perceel Nr. {index + 1}</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <span key={pid}>
                                                            <dt>
                                                                Id
                                                            </dt>
                                                            <dd>
                                                                {pid}
                                                            </dd>
                                                            <dt>
                                                                Oppervlakte
                                                            </dt>
                                                            <dd>
                                                                {parcelFeature.props.size.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ㎡
                                                            </dd>
                                                            <dt>
                                                                Sectie
                                                            </dt>
                                                            <dd>
                                                                {parcelFeature.props.section}
                                                            </dd>
                                                            {parcelFeature.props.zones &&
                                                                <>
                                                                    <dt>
                                                                        Zonering
                                                                    </dt>
                                                                    <dd>
                                                                        {getZoneTypeSetText(parcelFeature.props)}
                                                                    </dd>
                                                                </>
                                                            }
                                                            <dt>
                                                                Percel
                                                            </dt>
                                                            <dd>
                                                                {parcelFeature.props.parcel.toString()}
                                                            </dd>
                                                            {parcelFeature.props.dimensions &&
                                                                <>
                                                                    <dt>
                                                                        Max gebouw hoogtes
                                                                    </dt>
                                                                    <dd>
                                                                        {getDimensionMaxBuildingHeightsText(parcelFeature.props)}
                                                                    </dd>
                                                                </>
                                                            }
                                                        </span>
                                                    </AccordionDetails>
                                                </Accordion>
                                            )
                                        })}
                                    </div>}
                            </div>
                        </>
                    }

                </>
            }

        </div>
    )
};
export default SiteConf;
